/**
 * grid-filter.ts - the generic, cross-object grid filter contract (frontend
 * overhaul spec items 2 + 3). This is the shape every object grid serialises
 * into the URL search params and into a saved view. It supersedes the
 * prospector-specific `ProspectorFilter` for the new top-bar filter builder:
 * conditions are expressed over a `ColumnRef` (a native field or a field on an
 * associated object), combined with AND / OR groups.
 *
 * A `ColumnRef` is `{ source, field }` where `source` is `'self'` for a native
 * property or an association role (the registry edge's `toObject` name, or a
 * labelled role like `'owner'`) for an associated-object property. Example:
 * `{ source: 'company', field: 'annualRevenue' }` on a contacts grid reads the
 * contact's company's annual revenue (spec item 2: "Company > Annual Revenue").
 *
 * Owner: PLAN-04 frontend grids unit. PLAN-06 (charts drill) and PLAN-07 (email
 * drill-from-contacts) may consume this shape; the backend validates incoming
 * filter expressions with the same schema the grid builds.
 */

import { z } from 'zod'

// ── Column reference ──────────────────────────────────────────────────────────

/**
 * `'self'` for a native field, otherwise an association role resolved against
 * the object registry's association edges (one-cardinality edges expose the
 * related record's properties as columns).
 */
export const ColumnSourceSchema = z.string().min(1)
export type ColumnSource = z.infer<typeof ColumnSourceSchema>

export const ColumnRefSchema = z.object({
  source: ColumnSourceSchema.default('self'),
  field: z.string().min(1),
})
export type ColumnRef = z.infer<typeof ColumnRefSchema>

/** Stable string key for a ref, used as the ObjectGrid column id. */
export function columnRefKey(ref: ColumnRef): string {
  return `${ref.source}:${ref.field}`
}

/** Parse a `source:field` key back into a `ColumnRef` (source defaults to self). */
export function parseColumnRefKey(key: string): ColumnRef {
  const idx = key.indexOf(':')
  if (idx === -1) return { source: 'self', field: key }
  return { source: key.slice(0, idx) || 'self', field: key.slice(idx + 1) }
}

// ── Operators ─────────────────────────────────────────────────────────────────

export const FilterOperatorSchema = z.enum([
  'eq',
  'neq',
  'contains',
  'not_contains',
  'contains_any',
  'not_contains_any',
  'starts_with',
  'ends_with',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'not_in',
  'is_empty',
  'is_not_empty',
  'is_true',
  'is_false',
])
export type FilterOperator = z.infer<typeof FilterOperatorSchema>

/** Operators that take no value (the condition is complete on its own). */
export const VALUELESS_OPERATORS: ReadonlySet<FilterOperator> = new Set<FilterOperator>([
  'is_empty',
  'is_not_empty',
  'is_true',
  'is_false',
])

/** A field "kind" the builder uses to choose the operator set + input control. */
export const FilterFieldTypeSchema = z.enum([
  'text',
  'number',
  'money',
  'date',
  'boolean',
  'enum',
  'record',
])
export type FilterFieldType = z.infer<typeof FilterFieldTypeSchema>

/** Operators offered for each field kind, in menu order. */
export const OPERATORS_BY_TYPE: Record<FilterFieldType, FilterOperator[]> = {
  text: [
    'contains',
    'not_contains',
    'contains_any',
    'not_contains_any',
    'eq',
    'neq',
    'in',
    'not_in',
    'starts_with',
    'ends_with',
    'is_empty',
    'is_not_empty',
  ],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  money: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'],
  boolean: ['is_true', 'is_false'],
  enum: ['in', 'not_in', 'eq', 'neq', 'is_empty', 'is_not_empty'],
  record: ['in', 'not_in', 'eq', 'neq', 'is_empty', 'is_not_empty'],
}

/** Human label for an operator (used by the builder + active-filter chips). */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  not_contains: 'does not contain',
  contains_any: 'contains any of',
  not_contains_any: 'does not contain any of',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'greater than',
  gte: 'at least',
  lt: 'less than',
  lte: 'at most',
  between: 'between',
  in: 'is any of',
  not_in: 'is none of',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  is_true: 'is true',
  is_false: 'is false',
}

// ── Conditions, groups, expression ────────────────────────────────────────────

/** A scalar or list value (number ranges use a 2-element array with `between`). */
export const FilterValueSchema = z
  .union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.union([z.string(), z.number()])),
    z.null(),
  ])
  .optional()
export type FilterValue = z.infer<typeof FilterValueSchema>

export const FilterConditionSchema = z.object({
  id: z.string(),
  column: ColumnRefSchema,
  operator: FilterOperatorSchema,
  value: FilterValueSchema,
})
export type FilterCondition = z.infer<typeof FilterConditionSchema>

export const CombinatorSchema = z.enum(['and', 'or'])
export type Combinator = z.infer<typeof CombinatorSchema>

/** One AND/OR group of conditions (spec item 3: AND / OR groups). */
export const FilterGroupSchema = z.object({
  id: z.string(),
  combinator: CombinatorSchema.default('and'),
  conditions: z.array(FilterConditionSchema).default([]),
})
export type FilterGroup = z.infer<typeof FilterGroupSchema>

/**
 * The full filter expression: groups joined by a top-level combinator. A flat
 * single-group filter is the common case; nested AND-of-ORs / OR-of-ANDs is the
 * power case. Empty = "all rows".
 */
export const FilterExprSchema = z.object({
  combinator: CombinatorSchema.default('and'),
  groups: z.array(FilterGroupSchema).default([]),
})
export type FilterExpr = z.infer<typeof FilterExprSchema>

export const EMPTY_FILTER: FilterExpr = { combinator: 'and', groups: [] }

/** Count of leaf conditions across all groups (drives the toolbar badge). */
export function countConditions(expr: FilterExpr | undefined): number {
  if (!expr) return 0
  return expr.groups.reduce((sum, g) => sum + g.conditions.length, 0)
}

/**
 * Merge two filter expressions into one, AND-ing their groups together. Used
 * where a single grain evaluates the advanced filter AND the quick-filter chips
 * as one expression (e.g. the Project Prospector filters projects then
 * aggregates). Empties are dropped; if both are empty the result is empty.
 */
export function mergeFilterExprs(a: FilterExpr | undefined, b: FilterExpr | undefined): FilterExpr {
  const groups = [...(a?.groups ?? []), ...(b?.groups ?? [])].filter((g) => g.conditions.length > 0)
  if (groups.length === 0) return { combinator: 'and', groups: [] }
  return { combinator: 'and', groups }
}
